/*eslint-disable @typescript-eslint/no-var-requires*/
import React, { useEffect } from 'react';
import { BrowserRouter, Redirect, Switch, useLocation } from 'react-router-dom';
import { LayoutConfig } from '../config';
import { RoutesConfig } from '../config/routes';
import Layout from '../components/_base/Layout';
import { REGISTRATION_PATH, ROOT } from '../config/routes/paths';
import { useRoutes } from '../hooks/use-custom-routes';
import { IAuthContext, useAuth } from '../lib/context-providers/auth-context';
import { getUrlQueryArgByKey } from '../lib/api/utils';
import { BYPASS_TOKEN_KEY, CODE, ERROR_SUBDOMAIN, ML_ACCESS_TOKEN, STATE } from '../lib/constants';
import { AppConfigurator } from '../lib/services/app-configuration/AppConfigurator';
import useDefaultRoutePage from "../hooks/use-default-route-page";
import useSubdomain from '../hooks/use-subdomain';
import { ErrorPage } from './passport/ErrorPage';
import { EventSettingsContextType, useEventSettings } from '../lib/context-providers/event-settings-context/event-settings-context';
import { AccountContextType, useAccountContext } from '../lib/context-providers/account-context';
import { getFirstPathSegmentFromLocation, getPathSegmentListFromLocation, normalizeRoutePath } from '../lib/helpers/urlHelper';
import { IEventCustomSettings, useCustomSettings } from '../hooks/use-custom-settings';
import { usePathPrefix } from '../hooks/use-path-prefix';
import PasswordlessAuthProvider from '../lib/context-providers/paswordless-context/passwordless-context';

const { Route } = (() => {
  if (AppConfigurator.isPassportProfile) return require('../config/overrides/route');
  if (AppConfigurator.isMeetLinksProfile) return require('react-router-dom');
  if (AppConfigurator.isFileDownloadProfile) return require('react-router-dom');
})();

type Props = {
  routes: RoutesConfig
}

const RegistrationFormRoute = () => {
  const data = useEventSettings();
  const auth = useAuth() as IAuthContext;

  const accContext = useAccountContext() as AccountContextType;
  const noTokensInUrl = [ML_ACCESS_TOKEN, BYPASS_TOKEN_KEY, CODE, STATE].every(v => !getUrlQueryArgByKey(v));
  const registrationAsLandingPage = data && data.registrationAsLandingPage;

  const isRootPath = (): boolean => {
    const pathSegments = getPathSegmentListFromLocation();
    if (accContext.isAccountSubdomain) {
      return pathSegments.length === 1;
    } else {
      return pathSegments.length === 0;
    }
  };

  const path = getFirstPathSegmentFromLocation();
  const isRedirectedToRegistration = auth?.isAuthenticated === false && !auth.isLoading && !auth.signInAttempted && noTokensInUrl && isRootPath() && registrationAsLandingPage;
  const subdomain = useSubdomain() || '';
  const isRedirectedToErrorPage = subdomain === ERROR_SUBDOMAIN;

  if (isRedirectedToErrorPage) {
    return <ErrorPage/>;
  }
  if (isRedirectedToRegistration) {
    return <Redirect to={accContext.isAccountSubdomain ? normalizeRoutePath(REGISTRATION_PATH, path) : REGISTRATION_PATH} />;
  }
  return null;
};

const ScrollToTop = (): null => {
  const { pathname } = useLocation();

  useEffect(() => {
    const element = document.getElementsByClassName('router-wrapper')?.[0] as HTMLDivElement | undefined;
    if (element) {
      element.scrollTo({
        top: 0,
        behavior: 'instant' as ScrollBehavior,
      });
    }
  }, [pathname]);

  return null;
};

const RedirectToLandingPage = (): JSX.Element | null => {
  const landingPage = useDefaultRoutePage();
  const location = useLocation();
  const pathPrefix = usePathPrefix();
  const rootPath = normalizeRoutePath(ROOT, pathPrefix) || '/';
  const target = landingPage || rootPath;

  if (!target || target === location.pathname) {
    return null;
  }

  return (
    <Redirect
      to={{
        pathname: target,
        search: location.search
      }}
    />
  );
};

const PassportAppRouter: React.FC<Props> = (): JSX.Element => {
  const routes: RoutesConfig = { ...useRoutes() };
  const eventSettings = useEventSettings() as EventSettingsContextType;
  const customSettings = useCustomSettings() as IEventCustomSettings;
  const pathPrefix = usePathPrefix();

  return (
    <BrowserRouter>
      <PasswordlessAuthProvider>
        <Layout
          {...LayoutConfig}
          routesConfig={routes}
          customPages={eventSettings?.customPages}
          customSettings={customSettings}
          routePathPrefix={pathPrefix}
        >
          <ScrollToTop/>
          <RegistrationFormRoute/>
          { !!Object.keys(routes).length && (
            <Switch>
              {Object.keys(routes).map(path => {
                const routeProps = { ...routes[path] } as typeof routes[string] & { path?: string };
                delete routeProps.path;
                return <Route key={path} path={path} {...routeProps} />;
              })}
              {eventSettings?.pageFlagsAuthoritative === true && (
                <Route key='no-match'>
                  <RedirectToLandingPage />
                </Route>
              )}
            </Switch>
          )}
        </Layout>
      </PasswordlessAuthProvider>
    </BrowserRouter>
  );
};

/* used for meet links and file download apps (without Cognito authentication) */
const PublicAppRouter: React.FC<Props> = ({ routes }: Props): JSX.Element => {
  return (
    <BrowserRouter>
      <Layout {...LayoutConfig} routesConfig={routes}>
        <Switch>
          {Object.keys(routes).map(path => <Route key={path} {...{ path, ...routes[path] }} />)}
        </Switch>
      </Layout>
    </BrowserRouter>
  );
};

export default AppConfigurator.isPassportProfile ? PassportAppRouter : PublicAppRouter;